import { forwardRef, useId } from 'react';
import { CircleAlert } from 'lucide-react';
import { cn } from './cn';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Field label rendered above the textarea. */
  label?: React.ReactNode;
  /** Error message (string) or error flag (boolean). Renders the danger (red) validation treatment. */
  error?: string | boolean;
  /** Helper text below the textarea (hidden when there is an error). */
  helperText?: React.ReactNode;
  /** Stretch to fill the container width. Default true (matches MUI fullWidth usage). */
  fullWidth?: boolean;
  /** Class applied to the outer wrapper (label + field + helper). */
  containerClassName?: string;
  /** Visible number of text rows. Default 4. */
  rows?: number;
}

/**
 * Multi-line text input — replaces MUI <TextField multiline />.
 * Mirrors <Input /> styling (label + field + helper/error, token borders, focus-within
 * ring) for a <textarea>. Resizes vertically only. Forwards ref to the <textarea>.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = true,
      required,
      disabled,
      rows = 4,
      className,
      containerClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const textareaId = id ?? reactId;
    const hasError = Boolean(error);
    const errorText = typeof error === 'string' ? error : undefined;

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full', containerClassName)}>
        {label && (
          <label htmlFor={textareaId} className="text-sm font-medium text-content">
            {label}
            {required && <span className="ml-0.5 text-primary">*</span>}
          </label>
        )}

        <div
          className={cn(
            'group flex rounded-xl border bg-surface px-3.5 py-2.5 transition-all duration-200',
            // Field-validation errors use the danger (red) token + icon (CONVENTIONS §5).
            hasError
              ? 'border-danger ring-2 ring-danger/25 focus-within:border-danger focus-within:ring-danger/40'
              : 'border-line hover:border-primary/40 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30',
            disabled && 'opacity-60 pointer-events-none',
          )}
        >
          <textarea
            ref={ref}
            id={textareaId}
            rows={rows}
            required={required}
            disabled={disabled}
            aria-invalid={hasError || undefined}
            className={cn(
              'min-w-0 flex-1 resize-y bg-transparent text-sm text-content placeholder:text-muted/70 outline-none border-0',
              className,
            )}
            {...props}
          />
        </div>

        {errorText ? (
          <p className="flex items-start gap-1.5 text-xs font-medium text-danger">
            <CircleAlert size={13} strokeWidth={2.5} className="mt-px shrink-0" aria-hidden="true" />
            <span>{errorText}</span>
          </p>
        ) : helperText ? (
          <p className="text-xs text-muted">{helperText}</p>
        ) : null}
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
